import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  RefObject,
} from "react";
import clsx from "clsx";

import type { ServerDataSuccess } from "./route";
import { ProductCard } from "~/components/ProductCard";
import { Sheet } from "~/components/Sheet";

import { useRichText } from "~/hooks/useRichText";
import { useSheet } from "~/hooks/useSheet";
import { useProduct } from "./useProduct";

import { type Content } from "@ts/sanity";

import {
  TabbedSectionProps,
  TabbedSection,
  Button,
  IconChevron,
  ShopifyRichText,
} from "@bared/ui";

export interface FeatureAndShoeCareProps {
  /** Product name */
  productName: string | null;
  /** Gender of the product */
  gender?: "Mens" | "Womens";
}

export function FeatureAndShoeCareSheet(props: FeatureAndShoeCareProps) {
  const { shoeCareInstruction } = useProduct();
  const showShoeCare =
    !!shoeCareInstruction &&
    (props.gender === "Mens" || props.gender === "Womens");

  return (
    <div className="flex flex-col">
      <SheetTriggerButton title="Features" activeTab="feature" {...props} />
      {showShoeCare && (
        <SheetTriggerButton title="Shoe Care" activeTab="shoeCare" {...props} />
      )}
      <SheetTriggerButton
        title="Shipping & Returns"
        activeTab="shippingAndReturns"
        {...props}
      />
    </div>
  );
}

function SheetTriggerButton({
  title,
  activeTab,
  ...props
}: FeatureAndShoeCareProps & {
  title: string;
  activeTab: "feature" | "shoeCare" | "shippingAndReturns";
}) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const handleSheetToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, [isOpen]);

  return (
    <>
      <div>
        <Button
          variant="plain"
          size="medium"
          onClick={handleSheetToggle}
          className="flex w-full items-center justify-between border-b border-primary-600 py-mini pr-mini text-hint"
        >
          {title}
          <IconChevron className="w-[10px] rotate-[270deg] text-primary" />
        </Button>
      </div>

      <FeatureAndShoeCareSheetContent
        activeTab={activeTab}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        handleSheetToggle={handleSheetToggle}
        {...props}
      />
    </>
  );
}

export function FeatureAndShoeCareSheetContent({
  activeTab,
  isOpen,
  handleSheetToggle,
  setIsOpen,
  ...props
}: FeatureAndShoeCareProps & {
  activeTab: "feature" | "shoeCare" | "shippingAndReturns";
  isOpen: boolean;
  handleSheetToggle: () => void;
  setIsOpen: (isOpen: boolean) => void;
}) {
  const { shoeCareInstruction } = useProduct();
  const [sheetTitle, setSheetTitle] = useState<string | undefined>(undefined);

  const showShoeCare =
    !!shoeCareInstruction &&
    (props.gender === "Mens" || props.gender === "Womens");

  const tabs: TabbedSectionProps["tabs"] = [
    { title: "Features", handle: "feature" },
    ...(showShoeCare ? [{ title: "Shoe Care", handle: "shoeCare" }] : []),
    { title: "Shipping & Returns", handle: "shippingAndReturns" },
  ];
  const currentActiveTab = tabs.find((tab) => tab.handle === activeTab)?.title;

  const handleTitleChange = (tabHandle: string) => {
    setSheetTitle(tabs.find((tab) => tab.handle === tabHandle)?.title);
  };

  return (
    <>
      <Sheet
        renderHidden
        isOpen={isOpen}
        onClose={handleSheetToggle}
        title={sheetTitle}
        eventTitle={currentActiveTab}
      >
        <TabbedSheet
          {...props}
          tabs={tabs}
          onChange={handleTitleChange}
          activeTab={activeTab}
          handleSheetToggle={handleSheetToggle}
        />
      </Sheet>
    </>
  );
}

function TabbedSheet({
  onChange,
  activeTab,
  handleSheetToggle,
  tabs,
  ...props
}: FeatureAndShoeCareProps & {
  onChange: TabbedSectionProps["onChange"];
  activeTab: "feature" | "shoeCare" | "shippingAndReturns";
  handleSheetToggle: () => void;
  tabs: TabbedSectionProps["tabs"];
}) {
  const tabHandles = tabs.map((tab) => tab.handle);
  const { handleTabNavigation, sheetLinkClicks } = useSheet(tabHandles);

  const { parseContent } = useRichText();
  const {
    shopifyProduct: { descriptionHtml: features },
    template: { sheetFooterContent },
    shoeCareInstruction,
    shoeCareUpsellProducts,
    shippingAndReturnsSheetContents: shippingAndReturns,
  } = useProduct();

  const [_activeTab, setActiveTab] = useState<string>(activeTab);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const footerContent = parseContent(sheetFooterContent);
  const shoeCareGuide = parseContent(shoeCareInstruction?.instruction);

  /** Adds a click event listener to all links in the sheet container
   * to determine if the sheet should be closed or not
   */
  useEffect(() => {
    sheetLinkClicks(wrapperRef, handleSheetToggle);
  }, [wrapperRef, handleSheetToggle, sheetLinkClicks]);

  /** Adds a click event listener to elements with 'data-tab-to-navigate' attribute
   * to handle both internal tab navigation and external sheet opening
   */
  useEffect(() => {
    handleTabNavigation(wrapperRef, setActiveTab, onChange);
  }, [wrapperRef, onChange, setActiveTab, handleTabNavigation]);

  return (
    <>
      <TabbedSection
        tabs={tabs}
        renderHidden
        stickyTabTitle
        enableTabTitleScroll
        onChange={(tabHandle) => {
          setActiveTab(tabHandle);
          if (wrapperRef.current) {
            wrapperRef.current.scrollIntoView({
              behavior: "smooth",
              block: "start",
              inline: "nearest",
            });
          }
          onChange?.(tabHandle);
        }}
        activeTab={_activeTab}
        scrollableTabTitleClassName="scroll-px-midi md:scroll-px-maxi ps-midi md:ps-maxi"
      >
        {(tabHandle) => {
          if (tabHandle === "feature") {
            return (
              <FeatureSheetContent
                {...props}
                wrapperRef={wrapperRef}
                features={features}
                productName={props.productName}
              />
            );
          }
          if (tabHandle === "shoeCare") {
            return (
              <ShoeCareSheetContent
                wrapperRef={wrapperRef}
                productName={props.productName}
                shoeCareGuide={shoeCareGuide}
                shoeCareUpsellProducts={shoeCareUpsellProducts}
                handleSheetToggle={handleSheetToggle}
              />
            );
          }
          if (tabHandle === "shippingAndReturns") {
            return (
              <ShippingAndReturnsSheetContent
                wrapperRef={wrapperRef}
                shippingAndReturns={shippingAndReturns}
              />
            );
          }
        }}
      </TabbedSection>

      {!!footerContent && (
        <div className="mx-midi border-t border-primary-300 py-midi md:mx-maxi">
          {footerContent}
        </div>
      )}
    </>
  );
}

function FeatureSheetContent({
  wrapperRef,
  features,
  productName,
}: Pick<FeatureAndShoeCareProps, "productName"> & {
  features: ServerDataSuccess["shopifyProduct"]["descriptionHtml"];
  wrapperRef: RefObject<HTMLDivElement>;
}) {
  return (
    <div
      ref={wrapperRef}
      className="rte space-y-mini px-midi pb-midi md:px-maxi"
    >
      <h3 className="font-display text-h3 leading-h3 lg:text-[32px]">
        {productName ? `${productName} Features` : "Features"}
      </h3>

      <div dangerouslySetInnerHTML={{ __html: features }} />
    </div>
  );
}

function ShoeCareSheetContent({
  wrapperRef,
  productName,
  shoeCareGuide,
  shoeCareUpsellProducts,
  handleSheetToggle,
}: Pick<FeatureAndShoeCareProps, "productName"> & {
  shoeCareGuide: Content;
  shoeCareUpsellProducts: ServerDataSuccess["shoeCareUpsellProducts"];
  wrapperRef: RefObject<HTMLDivElement>;
  handleSheetToggle: () => void;
}) {
  return (
    <div ref={wrapperRef} className="space-y-mini px-midi pb-midi md:px-maxi">
      <h3 className="font-display text-h3 leading-h3 lg:text-[32px]">
        {productName ? `${productName} Shoe Care` : "Shoe Care"}
      </h3>

      {shoeCareUpsellProducts.length > 0 && (
        <div
          className={clsx(
            shoeCareUpsellProducts.length > 2 &&
              "scrollbar-hide -mx-maxi overflow-x-auto pe-maxi ps-maxi",
            "flex space-x-mini",
          )}
        >
          {shoeCareUpsellProducts.map((product, index) => {
            return (
              <div
                key={product.handle}
                className="flex w-[20%] min-w-[155px] flex-col [&_span]:text-caption"
              >
                <ProductCard
                  {...product}
                  index={index}
                  sizes="25vw"
                  enableAddToCart={true}
                  enableWishlist={false}
                  onClick={handleSheetToggle}
                  onAddToCart={handleSheetToggle}
                  className="flex h-full flex-col justify-between"
                />
              </div>
            );
          })}
        </div>
      )}

      {!!shoeCareGuide && <div>{shoeCareGuide}</div>}
    </div>
  );
}

function ShippingAndReturnsSheetContent({
  wrapperRef,
  shippingAndReturns,
}: {
  shippingAndReturns: ServerDataSuccess["shippingAndReturnsSheetContents"];
  wrapperRef: RefObject<HTMLDivElement>;
}) {
  if (!shippingAndReturns) return;

  const { parseContent } = useRichText();
  const shippingAndReturnsContent = parseContent(shippingAndReturns.content);

  return (
    <div ref={wrapperRef} className="space-y-mini px-midi pb-midi md:px-maxi">
      {shippingAndReturnsContent}
    </div>
  );
}
